import React, { useState } from 'react';
import {
  getAuth,
  createUserWithEmailAndPassword,
  updateProfile,
} from 'firebase/auth';
import { getFirestore, doc, setDoc } from 'firebase/firestore';
import { useNavigate } from 'react-router-dom';
import { useLoading } from '../../contexts/LoadingContext';
import { functionsService as functions } from '../../firebase';
import { httpsCallable } from 'firebase/functions';
import './RegisterPage.css';
import Logo from '../../assets/logo-green.svg';
import { isValidEmail } from '../../services/validations/emailValidation';
import { isValidCPF } from '../../services/validations/cpfValidation';

function RegisterPage() {
  const [nome, setNome] = useState('');
  const [email, setEmail] = useState('');
  const [senha, setSenha] = useState('');
  const [confirmarSenha, setConfirmarSenha] = useState('');
  const [cpf] = useState('122.287.020-72');
  const [plano, setPlano] = useState('Gratuito');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [passwordStrength, setPasswordStrength] = useState('');
  const navigate = useNavigate();
  const { loading, setLoading } = useLoading();

  const handlePasswordChange = (e) => {
    const value = e.target.value;
    setSenha(value);

    if (value.length < 6) {
      setPasswordStrength('Fraca');
    } else if (/[A-Z]/.test(value) && /[0-9]/.test(value)) {
      setPasswordStrength('Forte');
    } else {
      setPasswordStrength('Média');
    }
  };

  const notifyUser = (type, message) => {
    if (type === 'error') {
      setError(message);
    } else if (type === 'success') {
      setSuccess(message);
    }
  };

  const createUserProfile = async (
    auth,
    db,
    nome,
    email,
    senha,
    cpf,
    plano
  ) => {
    const userCredential = await createUserWithEmailAndPassword(
      auth,
      email,
      senha
    );
    const user = userCredential.user;

    await updateProfile(user, { displayName: nome });

    await setDoc(doc(db, 'users', user.uid), {
      nome,
      email,
      cpf,
      uid: user.uid,
      plano: plano,
      createdAt: new Date(),
    });
  };

  const initiateStripePayment = async (nome, email, cpf, plano) => {
    const createSubscription = httpsCallable(functions, 'createSubscription'); // Usar diretamente o httpsCallable
    return await createSubscription({ nome, email, cpf, plano });
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    setLoading(true);
    notifyUser('error', '');
    notifyUser('success', '');

    if (!isValidEmail(email)) {
      notifyUser('error', 'Por favor, insira um email válido.');
      setLoading(false);
      return;
    }

    if (!isValidCPF(cpf)) {
      notifyUser('error', 'Por favor, insira um CPF válido.');
      setLoading(false);
      return;
    }

    if (senha !== confirmarSenha) {
      notifyUser('error', 'As senhas não coincidem.');
      setLoading(false);
      return;
    }

    if (senha.length < 6) {
      notifyUser('error', 'A senha deve ter no mínimo 6 caracteres.');
      setLoading(false);
      return;
    }

    try {
      const auth = getAuth();
      const db = getFirestore();

      if (plano !== 'Gratuito') {
        const result = await initiateStripePayment(nome, email, cpf, plano);

        if (result.data.status === 'success') {
          window.location.assign(result.data.url);
        } else {
          notifyUser(
            'error',
            'Falha no pagamento. Por favor, tente novamente ou escolha outro método de pagamento.'
          );
          setLoading(false);
          return;
        }
      } else {
        await createUserProfile(auth, db, nome, email, senha, cpf, plano);
        setLoading(false);
        notifyUser(
          'success',
          'Cadastro realizado com sucesso! Redirecionando para o login...'
        );
        setTimeout(() => {
          navigate('/login');
        }, 4000);
      }
    } catch (error) {
      console.error('Erro ao criar conta:', error.message);
      if (error.code === 'auth/email-already-in-use') {
        notifyUser(
          'error',
          'O e-mail já está em uso. Por favor, use outro e-mail.'
        );
      } else if (error.code === 'functions/internal') {
        notifyUser(
          'error',
          'Erro interno no servidor. Tente novamente mais tarde.'
        );
      } else {
        notifyUser(
          'error',
          'Houve um problema ao realizar o cadastro. Por favor, tente novamente.'
        );
      }
      setLoading(false);
    }
  };

  return (
    <div className="register-container" role="main">
      <div className="register-card">
        <div className="logo-container">
          <img src={Logo} alt="Plantão Fácil Logo" className="login-logo" />
        </div>
        <h2 className="register-title">Criar Conta</h2>
        <p className="register-subtitle">
          Comece a gerenciar seus plantões de forma prática e organizada
        </p>
        {error && (
          <div
            className="error-message"
            aria-live="polite"
            data-testid="error-message"
          >
            {error}
          </div>
        )}
        {success && (
          <div
            className="success-message"
            aria-live="polite"
            data-testid="success-message"
          >
            {success}
          </div>
        )}
        <form
          onSubmit={handleRegister}
          className="register-form"
          data-testid="register-form"
        >
          <div className="form-row">
            <div className="input-group">
              <label htmlFor="nome">Nome completo:</label>
              <input
                type="text"
                id="nome"
                name="nome"
                placeholder="Digite seu nome completo"
                value={nome}
                onChange={(e) => setNome(e.target.value)}
                required
                disabled={loading}
                aria-required="true"
                data-testid="nome-input"
              />
            </div>

            <div className="input-group">
              <label htmlFor="email">Email:</label>
              <input
                type="email"
                id="email"
                name="email"
                placeholder="Digite seu email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                disabled={loading}
                aria-required="true"
                data-testid="email-input"
              />
            </div>
          </div>

          <div className="form-row">
            <div className="input-group">
              <label htmlFor="senha">Senha:</label>
              <input
                type="password"
                id="senha"
                name="senha"
                placeholder="Escolha uma senha"
                value={senha}
                onChange={handlePasswordChange}
                required
                disabled={loading}
                aria-required="true"
                data-testid="senha-input"
              />
              <small
                className={`password-strength ${passwordStrength.toLowerCase()}`}
                data-testid="password-strength"
              >
                Força da senha: {passwordStrength}
              </small>
            </div>

            <div className="input-group">
              <label htmlFor="confirmarSenha">Confirme a Senha:</label>
              <input
                type="password"
                id="confirmarSenha"
                name="confirmarSenha"
                placeholder="Confirme sua senha"
                value={confirmarSenha}
                onChange={(e) => setConfirmarSenha(e.target.value)}
                required
                disabled={loading}
                aria-required="true"
                data-testid="confirmarSenha-input"
              />
            </div>
          </div>

          <div className="form-row">
            <div className="input-group">
              <label htmlFor="plano">Selecione seu Plano:</label>
              <select
                id="plano"
                name="plano"
                value={plano}
                onChange={(e) => setPlano(e.target.value)}
                required
                disabled={loading}
                aria-required="true"
                data-testid="plano-select"
              >
                <option value="Gratuito">Gratuito</option>
                <option value="Premium">Premium</option>
                <option value="Empresarial">Empresarial</option>
              </select>
            </div>
          </div>

          <button
            type="submit"
            className="register-button"
            disabled={loading}
            data-testid="register-button"
          >
            {loading ? 'Cadastrando...' : 'Criar Conta'}
          </button>

          <div className="login-footer">
            <a href="/login" className="login-link" data-testid="login-link">
              Já possui uma conta? Faça login
            </a>
          </div>
        </form>
      </div>
    </div>
  );
}

export default RegisterPage;
